/*
 * The admin auth context: the one place the console's React tree learns whether an operator is
 * signed in, who they are, and whether they still owe a forced reset. The raw token lives in the
 * non-React session store (sessionStorage); this provider holds only the decoded claims in state and
 * exposes `getToken()` for the API client. Sign-in persists the token and decodes it; sign-out
 * clears it (logout is a client-side drop — the JWT is stateless). Initial state is seeded from the
 * store so a page reload within the tab stays signed in.
 *
 * A tab that boots with an empty store has one more step before it can answer "signed in?": it asks
 * the tabs already open for the token (admin-session-sync, CAE-576), because a tab opened by
 * cmd-click inherits nothing. That makes the answer asynchronous for the first moment of a tab's
 * life, which is what `isResolving` exists to express — and why a guard must not read a null
 * `claims` as "signed out" until it clears. The same channel carries the end of a session in the
 * other direction: a logout here, or an API 401 anywhere, drops it in every tab.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createAdminSessionStore, type AdminSessionStore } from './admin-session-store';
import {
  createAdminSessionSync,
  type AdminSessionSync,
  type AdminSessionSyncOptions,
} from './admin-session-sync';
import { decodeAdminClaims, type AdminClaims } from './admin-token';
import { subscribeUnauthorized } from './unauthorized-signal';

/** Builds the cross-tab sync around the provider's own handlers; injectable for tests. */
export type AdminSessionSyncFactory = (
  handlers: Pick<AdminSessionSyncOptions, 'readToken' | 'onRemoteSignOut'>,
) => AdminSessionSync;

export interface AdminAuth {
  /** The decoded claims of the signed-in operator, or null when signed out. */
  readonly claims: AdminClaims | null;
  /**
   * True while a tab that booted with an empty store is still asking the open tabs for a token.
   * A guard must wait this out rather than treating it as signed out (CAE-576).
   */
  readonly isResolving: boolean;
  /** Persist and adopt a freshly issued admin token (login or reset). */
  readonly signIn: (token: string) => void;
  /** Drop the token and return to a signed-out state (logout). */
  readonly signOut: () => void;
  /** The raw token for attaching to admin API calls, or null when signed out. */
  readonly getToken: () => string | null;
}

const AdminAuthContext = createContext<AdminAuth | null>(null);

/** The admin auth context. Throws if used outside an `<AdminAuthProvider>`. */
export function useAdminAuth(): AdminAuth {
  const auth = useContext(AdminAuthContext);
  if (auth === null) {
    throw new Error('useAdminAuth must be used inside an <AdminAuthProvider>');
  }
  return auth;
}

export interface AdminAuthProviderProps {
  readonly children: ReactNode;
  /** Injected for tests; defaults to the sessionStorage-backed store. */
  readonly store?: AdminSessionStore;
  /** Injected for tests; defaults to a real BroadcastChannel-backed sync. */
  readonly createSync?: AdminSessionSyncFactory;
}

export function AdminAuthProvider({ children, store, createSync }: AdminAuthProviderProps) {
  const storeRef = useRef<AdminSessionStore | null>(store ?? null);
  storeRef.current ??= createAdminSessionStore();
  const sessionStore = storeRef.current;

  const [claims, setClaims] = useState<AdminClaims | null>(() => {
    const existing = sessionStore.read();
    return existing === null ? null : decodeAdminClaims(existing);
  });

  // A tab that booted with a token of its own has nothing to resolve; only an empty one asks around.
  const [isResolving, setIsResolving] = useState(() => sessionStore.read() === null);

  const syncRef = useRef<AdminSessionSync | null>(null);
  const buildSync = createSync ?? createAdminSessionSync;

  /*
   * Three things end a session, and they differ only in who else needs telling. Ending it *here* is
   * this one operation; whether it is also announced is the caller's call — a sign-out that arrived
   * over the channel must not be echoed back, or two tabs would talk past each other.
   */
  const endSessionHere = useCallback(() => {
    sessionStore.clear();
    setClaims(null);
  }, [sessionStore]);

  useEffect(() => {
    const sync = buildSync({
      readToken: () => sessionStore.read(),
      onRemoteSignOut: endSessionHere,
    });
    syncRef.current = sync;

    // The API refusing the token settles it for every tab, not just this one: they all hold the
    // same token, so the others are about to be refused too. Telling them now turns one operator's
    // expiry into a single trip through the login screen instead of one per tab.
    const stopWatchingRefusals = subscribeUnauthorized(() => {
      endSessionHere();
      sync.broadcastSignOut();
    });

    let abandoned = false;
    if (sessionStore.read() === null) {
      void sync.requestToken().then((token) => {
        if (abandoned) {
          return;
        }
        if (token !== null) {
          sessionStore.write(token);
          setClaims(decodeAdminClaims(token));
        }
        setIsResolving(false);
      });
    }

    return () => {
      abandoned = true;
      syncRef.current = null;
      stopWatchingRefusals();
      sync.close();
    };
    // Runs once for the provider's lifetime: the store and the sync factory are both fixed at the
    // composition root, and re-running would tear down the channel other tabs are talking to.
  }, []);

  const signIn = useCallback(
    (token: string) => {
      sessionStore.write(token);
      setClaims(decodeAdminClaims(token));
    },
    [sessionStore],
  );

  // Logging out is a decision about the operator's session, not about this tab: all the tabs share
  // the one token, so leaving the others signed in would defeat the point on a shared machine.
  const signOut = useCallback(() => {
    endSessionHere();
    syncRef.current?.broadcastSignOut();
  }, [endSessionHere]);

  const getToken = useCallback(() => sessionStore.read(), [sessionStore]);

  const value = useMemo<AdminAuth>(
    () => ({ claims, isResolving, signIn, signOut, getToken }),
    [claims, isResolving, signIn, signOut, getToken],
  );

  return <AdminAuthContext value={value}>{children}</AdminAuthContext>;
}
